import { getAxiosInstance } from "../utils/apiClient";
import { getTokens } from "../utils/tokens";

const axiosInstance = getAxiosInstance();

/**
 * Initialize cryptographic keys for the authenticated user
 * Should be called once when the user first enables E2EE
 * @returns {Promise<{success: boolean, message: string, data: {registrationId: number, deviceId: number}}>}
 */
export const initializeUserKeysAPI = () => {
  return new Promise((resolve, reject) => {
    const tokens = getTokens();

    axiosInstance
      .post("/api/e2ee/initialize", {}, {
        headers: {
          "Content-Type": "application/json",
          [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
          [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
            `Bearer ${tokens.firebase_token}`,
          "ngrok-skip-browser-warning": "true",
        },
      })
      .then((res) => resolve(res.data))
      .catch((err) => reject(err?.response?.data || err));
  });
};

/**
 * Get public key bundle for a user
 * Used for initializing a new E2EE session
 * @param {string} targetUserId - The user ID to get the key bundle for
 * @returns {Promise<{success: boolean, message: string, data: {identityKey: string, signedPrekey: object, oneTimePrekeys: array, registrationId: number}}>}
 */
export const getUserKeyBundleAPI = (targetUserId) => {
  return new Promise((resolve, reject) => {
    const tokens = getTokens();

    axiosInstance
      .get(`/api/e2ee/key-bundle/${targetUserId}`, {
        headers: {
          "Content-Type": "application/json",
          [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
          [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
            `Bearer ${tokens.firebase_token}`,
          "ngrok-skip-browser-warning": "true",
        },
      })
      .then((res) => resolve(res.data))
      .catch((err) => reject(err?.response?.data || err));
  });
};

/**
 * Initialize a new E2EE session with another user using X3DH key agreement
 * @param {string} recipientId - The recipient user ID
 * @param {string} conversationId - The conversation ID
 * @param {object} keyBundle - The recipient's key bundle
 * @returns {Promise<{success: boolean, message: string, data: {sessionId: string, conversationId: string}}>}
 */
export const initializeE2EESessionAPI = (recipientId, conversationId, keyBundle) => {
  return new Promise((resolve, reject) => {
    const tokens = getTokens();

    axiosInstance
      .post(
        "/api/e2ee/initialize-session",
        {
          recipientId,
          conversationId,
          keyBundle,
        },
        {
          headers: {
            "Content-Type": "application/json",
            [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
            [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
              `Bearer ${tokens.firebase_token}`,
            "ngrok-skip-browser-warning": "true",
          },
        }
      )
      .then((res) => resolve(res.data))
      .catch((err) => reject(err?.response?.data || err));
  });
};

/**
 * Check if an E2EE session exists for a conversation
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<{success: boolean, message: string, data: {hasSession: boolean, isInitialized: boolean, sessionId: string, lastMessageAt: string}}>}
 */
export const getE2EESessionStatusAPI = (conversationId) => {
  return new Promise((resolve, reject) => {
    const tokens = getTokens();

    axiosInstance
      .get(`/api/e2ee/session/${conversationId}`, {
        headers: {
          "Content-Type": "application/json",
          [process.env.REACT_APP_AUTH_TOKEN]: `Bearer ${tokens.access_token}`,
          [process.env.REACT_APP_FIREBASE_AUTH_TOKEN]:
            `Bearer ${tokens.firebase_token}`,
          "ngrok-skip-browser-warning": "true",
        },
      })
      .then((res) => resolve(res.data))
      .catch((err) => reject(err?.response?.data || err));
  });
};

